import { useState } from 'react';
import { CollapsibleCard } from '../primitives/CollapsibleCard';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { FaqItem } from '../../types';

const FAQS: readonly FaqItem[] = [
  {
    q: '"Is this just going to sound like AI?"',
    a: "Human-checked before it goes live. Your voice, not raw AI. Before anything is written I interview you and document three things, your voice, your business (offers, pricing, who you sell to, the language you use), and your objections. Then the system drafts against your own recorded sales calls, and I read every piece before it publishes. The reason generic AI content reads as fake is that it has no idea what your business actually is. This one does.",
  },
  {
    q: '"I don’t want to change my current systems."',
    a: "You don't have to. The system works around what you already run, your forms, your calendar, your inbox, your CRM. Nothing gets ripped out and replaced. If you leave, the files, the history and the reports go with you, so there is no version of this where you end up trapped in someone else's platform.",
  },
  {
    q: '"I’ve tried AI before and it didn’t work."',
    a: "That is the normal experience, and it is usually the same cause. A generic tool with no context on your business produces output that takes longer to fix than it saves. The difference here is what the system is grounded in, your competitors' actual content gaps and your own recorded objections, plus me reading it before it goes out. Same technology, completely different input.",
  },
  {
    q: '"What actually happens in the first 14 days?"',
    a: "I interview you and build the Directive Files, run the competitor scan across your market, mine your existing calls and email chains for the objections that keep coming up, stand up the dashboard and the ideation pipeline, and queue your first content batch. If that is not live within 14 days, or if 20 pieces are not published inside 30 days, that month's retainer is credited.",
  },
  {
    q: '"Why is there a revenue share on ads?"',
    a: "Because it is the only part of this where my incentives should be tied to a number. Content, copywriting and production sit inside the retainer with no share attached, so I am never quietly pushing you toward more spend to get paid more on the rest. Only money attributable to Meta ad spend carries it.",
  },
  {
    q: '"What about my data?"',
    a: "Your business info only, not your customers' data. It is used to ground the content in how you actually talk and what you actually sell, and it is deleted on request. Nothing about your business gets used in another client's content, and I never name one client to another without explicit approval.",
  },
  {
    q: '"Do I have to be on camera?"',
    a: "Most of the time, no. Around 80% of what I need is photos and short clips of you or your team actually working, filmed on a phone in the middle of a job you were doing anyway. Nobody has to present to camera for that, and it does not have to be you, employee-generated content works just as well. It is only the video pieces, where your credibility is what does the selling, that need you talking, and that is genuinely just pulling out your phone for two minutes. No lighting, no crew, no script, because raw outperforms produced here. If nobody wants to do even that, I bring in a fractional creator to fill the slot. Everything else, the reviews, the follow-ups, the writing and the scheduling, is handled either way.",
  },
];

export function FAQSection() {
  // Only one entry is open at a time, so this is a single index, not a set.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(8,8,8,0.88)' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <SectionLabel number="-">FAQ</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>The questions worth answering honestly.</SectionH2>
          </div>
        </FadeUp>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQS.map((faq, idx) => (
            <FadeUp key={faq.q} delay={idx * 50}>
              <CollapsibleCard
                title={faq.q}
                isOpen={openIndex === idx}
                onToggle={() => setOpenIndex(openIndex === idx ? null : idx)}
              >
                {faq.a}
              </CollapsibleCard>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
